import {createApi} from '@reduxjs/toolkit/query/react'
import type {ISuccessMessage} from '~/src/models'
import type {
  CreateDeductionRequest,
  DeductionResponse,
} from '../../components/candidate-tax/deductions/types'
import type {
  Activity,
  AddBankAccountRequest,
  AddQuickNoteRequest,
  Applicant,
  ApplicantInterview,
  ApplicantsRequest,
  ApplicantsResponse,
  ApplicantsWithAssignmentsByEorRequest,
  ApplicantWithAssignments,
  BankAccount,
  CreateApplicantInterviewRequestBody,
  EventAttachment,
  ExclusionListRequest,
  ISkillsByCategory,
  QuickNote,
  UpdateApplicantInterviewRequestBody,
  UpdateBankAccountRequest,
  UpdateQuickNoteRequest,
  WorkHistory,
} from '../../models/applicant'
import {baseQuery} from '../base-query'

export interface TransferBlocker {
  type: string
  message: string
  count?: number
  details?: string[]
}

export interface TransferWarning {
  type: string
  message: string
  count?: number
}

export interface TransferEligibilityResponse {
  eligible: boolean
  blockers: TransferBlocker[]
  warnings: TransferWarning[]
}

export interface TransferResultResponse {
  success: boolean
  applicantId: string
  previousAgencyId: string
  newAgencyId: string
  previousAgencyName?: string
  newAgencyName?: string
  payrollOrgChanged: boolean
  transferredAt: string
  effectiveDate?: string
}

export const applicantsApi = createApi({
  reducerPath: 'ApplicantsApi',
  // 'QuickNotes' is keyed per candidate — see getQuickNotes for why it is separate from 'Applicants'.
  tagTypes: ['Applicants', 'ApplicantSkills', 'QuickNotes'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getApplicants: builder.query<ApplicantsResponse, ApplicantsRequest>({
      providesTags: ['Applicants'],
      query: ({clientsApplicantWorkedFor, ...rest}) => ({
        url: `/applicants/getApplicants`,
        method: 'POST',
        params: {agencyId: rest?.agencyId?.length > 0 ? rest?.agencyId : undefined},
        body: {
          clientsApplicantWorkedFor:
            clientsApplicantWorkedFor?.length === 0 ? undefined : clientsApplicantWorkedFor,
          ...rest,
          agencyId: undefined,
        },
      }),
      keepUnusedDataFor: 1,
    }),

    searchApplicantsIsolated: builder.query<ApplicantsResponse, ApplicantsRequest>({
      query: ({clientsApplicantWorkedFor, ...rest}) => ({
        url: `/applicants/getApplicants`,
        method: 'POST',
        params: {agencyId: rest?.agencyId?.length > 0 ? rest?.agencyId : undefined},
        body: {
          clientsApplicantWorkedFor:
            clientsApplicantWorkedFor?.length === 0 ? undefined : clientsApplicantWorkedFor,
          ...rest,
          agencyId: undefined,
        },
      }),
      keepUnusedDataFor: 0,
    }),

    /**
     * Get applicants with assignments by EOR
     * POST /applicants/getApplicantsWithAssignmentsByEor
     *
     * Returns applicants belonging to the EOR agency (and its non-EOR child agencies if parent)
     * with their assignments filtered by the specified company.
     * Supports search by name/SSN and alphabetical sorting.
     * Platform Admin only.
     */
    getApplicantsWithAssignmentsByEor: builder.query<
      ApplicantWithAssignments[],
      ApplicantsWithAssignmentsByEorRequest
    >({
      providesTags: ['Applicants'],
      query: (body) => ({
        url: `/applicants/getApplicantsWithAssignmentsByEor`,
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 1,
    }),

    getApplicant: builder.query<Applicant, {id: string; agencyId?: string}>({
      providesTags: ['Applicants'],
      query: ({id, agencyId}) => ({
        url: `/applicants/getApplicantById/${id}`,
        params: {agencyId},
        method: 'GET',
      }),
    }),

    updateApplicant: builder.mutation<Applicant, {applicant: Applicant}>({
      invalidatesTags: ['Applicants'],
      query: ({applicant}) => ({
        url: `/applicants/updateApplicant`,
        method: 'PUT',
        body: applicant,
      }),
    }),

    lockApplicant: builder.query<
      {id: string; clientId: string; status: string; date: Date},
      {id: string; clientId: string}
    >({
      providesTags: ['Applicants'],
      query: (params) => ({
        url: `/applicants/applicantLock`,
        method: 'GET',
        params: params,
      }),
    }),

    unLockApplicant: builder.query<
      {id: string; clientId: string; status: string; date: Date},
      {id: string; clientId: string}
    >({
      providesTags: ['Applicants'],
      query: (params) => ({
        url: `/applicants/applicantUnlock`,
        method: 'GET',
        params: params,
      }),
    }),

    addApplicantsActivity: builder.mutation<
      ISuccessMessage,
      {activity: Activity; agencyId?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({activity, agencyId}) => ({
        url: `/applicants/addApplicantsActivity`,
        method: 'POST',
        body: activity,
        params: {agencyId},
      }),
    }),

    getApplicantsExclusionList: builder.query<Partial<Applicant>[], ExclusionListRequest>({
      providesTags: ['Applicants'],
      query: ({pageNumber, pageSize, applicantIds, agencyId}) => ({
        url: '/applicants/getApplicantExclusionList',
        method: 'POST',
        body: {
          pageNumber,
          pageSize,
          applicantIds,
        },
        params: {agencyId},
      }),
    }),

    getApplicantListByIds: builder.query<Applicant[], {applicantIds: string[]; agencyId?: string}>({
      providesTags: ['Applicants'],
      query: ({applicantIds, agencyId}) => ({
        url: '/applicants/getApplicantListByIds',
        method: 'POST',
        body: applicantIds,
        params: {agencyId},
      }),
    }),

    moveApplicantToNewAgency: builder.mutation<
      Applicant,
      {applicantId: string; newAgencyId: string; loggedInAgencyId?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, newAgencyId, loggedInAgencyId}) => ({
        url: '/applicants/moveApplicantToNewAgency',
        method: 'POST',
        params: {applicantId, agencyId: loggedInAgencyId},
        body: {newAgencyId},
      }),
    }),

    getTransferEligibility: builder.query<TransferEligibilityResponse, string>({
      query: (applicantId) => ({
        url: `/applicants/transfer/${applicantId}/eligibility`,
        method: 'GET',
      }),
    }),

    transferApplicant: builder.mutation<
      TransferResultResponse,
      {applicantId: string; newAgencyId: string; reason: string; effectiveDate?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, newAgencyId, reason, effectiveDate}) => ({
        url: `/applicants/transfer/${applicantId}`,
        method: 'POST',
        body: {newAgencyId, reason, effectiveDate},
      }),
    }),

    getApplicantInterview: builder.query<ApplicantInterview, string>({
      providesTags: ['Applicants'],
      query: (agencyId) => ({
        url: `/applicants/getApplicantInterview`,
        method: 'GET',
        params: {agencyId},
      }),
    }),

    createApplicantInterview: builder.mutation<
      ApplicantInterview,
      {agencyId?: string; body: CreateApplicantInterviewRequestBody}
    >({
      invalidatesTags: ['Applicants'],
      query: ({agencyId, body}) => ({
        url: `/applicants/createApplicantInterview`,
        method: 'POST',
        params: {agencyId},
        body: body,
      }),
    }),

    updateApplicantInterview: builder.mutation<
      ApplicantInterview,
      {agencyId?: string; body: UpdateApplicantInterviewRequestBody}
    >({
      invalidatesTags: ['Applicants'],
      query: ({agencyId, body}) => ({
        url: `/applicants/updateApplicantInterview`,
        method: 'PUT',
        params: {agencyId},
        body: body,
      }),
    }),

    uploadApplicantProfilePicture: builder.mutation<
      ApplicantInterview,
      {applicantId: string; body: File; agencyId?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, body, agencyId}) => {
        const formData = new FormData()
        formData.append('file', body)
        return {
          url: `/applicants/uploadProfilePicture/${applicantId}`,
          method: 'PUT',
          params: {applicantId, agencyId},
          body: formData,
          formData: true,
        }
      },
    }),

    deleteApplicantProfilePicture: builder.mutation<
      ApplicantInterview,
      {applicantId: string; agencyId?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, agencyId}) => {
        return {
          url: `/applicants/deleteProfilePicture`,
          method: 'PUT',
          params: {applicantId, agencyId},
        }
      },
    }),

    updateApplicantFlag: builder.mutation<
      {applicantId: string; flag: string; agencyId?: string},
      {applicantId: string; flag: string; agencyId?: string}
    >({
      // No invalidatesTags: the getApplicants LIST endpoint is search-indexed and lags
      // this write, so auto-refetching it would overwrite the just-set flag with the
      // stale indexed value. The list is patched optimistically (patchApplicantFlag) and
      // the open profile is refetched by-id (immediately consistent) by the caller.
      query: ({applicantId, flag, agencyId}) => ({
        url: `/applicants/updateApplicantFlag`,
        method: 'PUT',
        params: {applicantId, flag, agencyId},
        body: {},
      }),
    }),

    // Banking endpoints
    getBankAccounts: builder.query<BankAccount[], string>({
      providesTags: ['Applicants'],
      query: (applicantId) => ({
        url: `/applicants/${applicantId}/bank-accounts`,
        method: 'GET',
      }),
    }),

    addBankAccount: builder.mutation<
      BankAccount,
      {applicantId: string; data: AddBankAccountRequest}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, data}) => ({
        url: `/applicants/${applicantId}/bank-accounts`,
        method: 'POST',
        body: data,
      }),
    }),

    updateBankAccount: builder.mutation<
      BankAccount,
      {applicantId: string; accountIndex: number; data: UpdateBankAccountRequest}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, accountIndex, data}) => ({
        url: `/applicants/${applicantId}/bank-accounts/${accountIndex}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteBankAccount: builder.mutation<void, {applicantId: string; accountIndex: number}>({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, accountIndex}) => ({
        url: `/applicants/${applicantId}/bank-accounts/${accountIndex}`,
        method: 'DELETE',
      }),
    }),

    // Deduction endpoints
    addDeduction: builder.mutation<
      DeductionResponse,
      {applicantId: string; data: CreateDeductionRequest; agencyId?: string; companyId?: string}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, data, agencyId, companyId}) => ({
        url: `/applicants/${applicantId}/deductions`,
        method: 'POST',
        body: data,
        params: {agencyId, companyId},
      }),
    }),

    updateDeduction: builder.mutation<
      DeductionResponse,
      {
        applicantId: string
        deductionId: string
        data: CreateDeductionRequest
        agencyId?: string
        companyId?: string
      }
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, deductionId, data, agencyId, companyId}) => ({
        url: `/applicants/${applicantId}/deductions/${deductionId}`,
        method: 'PUT',
        body: data,
        params: {agencyId, companyId},
      }),
    }),

    updateDeductionStatus: builder.mutation<
      DeductionResponse,
      {
        applicantId: string
        deductionId: string
        status: 'Active' | 'Inactive' | 'Archived'
        agencyId?: string
        companyId?: string
      }
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, deductionId, status, agencyId, companyId}) => ({
        url: `/applicants/${applicantId}/deductions/${deductionId}/status`,
        method: 'PUT',
        body: {status},
        params: {agencyId, companyId},
      }),
    }),

    // Work History Bulk Add endpoint
    // Used to add multiple work histories at once instead of calling updateApplicant
    // verifiedInStaffology should always be false for new work histories added via UI
    // Items with verifiedInStaffology=true are hidden from the UI (managed by Staffology)
    addWorkHistoryBulk: builder.mutation<
      Applicant,
      {applicantId: string; workHistories: Omit<WorkHistory, 'verifiedInStaffology'>[]}
    >({
      invalidatesTags: ['Applicants'],
      query: ({applicantId, workHistories}) => ({
        url: `/applicants/addWorkHistoryBulk/${applicantId}`,
        method: 'POST',
        body: {
          workHistories: workHistories.map((wh) => ({...wh, verifiedInStaffology: false})),
        },
      }),
    }),

    uploadEventAttachment: builder.mutation<
      Pick<EventAttachment, 'fileName' | 'fileUrl' | 'fileKey' | 'fileSize'>,
      {applicantId: string; file: File; agencyId?: string}
    >({
      query: ({applicantId, file, agencyId}) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `/applicants/uploadEventAttachment/${applicantId}`,
          method: 'POST',
          params: agencyId ? {agencyId} : undefined,
          body: formData,
          formData: true,
        }
      },
    }),

    getEventAttachmentDownloadUrl: builder.query<
      {downloadUrl: string},
      {applicantId: string; fileKey: string}
    >({
      query: ({applicantId, fileKey}) => ({
        url: `/applicants/downloadEventAttachment/${applicantId}`,
        params: {fileKey},
      }),
    }),

    getApplicantSkills: builder.query<{skills: ISkillsByCategory[]}, {id: string}>({
      providesTags: (_result, _error, {id}) => [{type: 'ApplicantSkills' as const, id}],
      query: ({id}) => ({
        url: `/applicants/${id}/skills`,
        method: 'GET',
      }),
    }),

    // FULL-REPLACE: send the complete resulting list; adds/removals are implicit.
    // The backend validates every skill against the master catalog (400 lists
    // unknown skills) and pushes the change to Ollyboard after saving.
    updateApplicantSkills: builder.mutation<
      {success: boolean; skills: ISkillsByCategory[]},
      {id: string; skills: ISkillsByCategory[]}
    >({
      invalidatesTags: (_result, _error, {id}) => [
        'Applicants',
        {type: 'ApplicantSkills' as const, id},
      ],
      query: ({id, skills}) => ({
        url: `/applicants/${id}/skills`,
        method: 'PUT',
        body: {skills},
      }),
    }),

    // Quick Notes (STFFLG-4077 / STFFLG-4078) — their own endpoints; the whole-applicant save no longer
    // carries notes. Deliberately a per-candidate 'QuickNotes' tag, NOT 'Applicants': refetching the
    // candidate re-initialises the Events tab's Formik (enableReinitialize), which would wipe an unsaved
    // Event draft whenever a note is saved beneath it. The server-written Activity tab entry shows up
    // on the candidate's next refetch; the Activity Log panel loads fresh when opened.
    getQuickNotes: builder.query<QuickNote[], string>({
      providesTags: (_result, _error, applicantId) => [
        {type: 'QuickNotes' as const, id: applicantId},
      ],
      query: (applicantId) => ({
        url: `/applicants/${applicantId}/quick-notes`,
        method: 'GET',
      }),
    }),

    // The server stamps the author and time; the response is the saved note.
    addQuickNote: builder.mutation<QuickNote, {applicantId: string; note: AddQuickNoteRequest}>({
      invalidatesTags: (_result, _error, {applicantId}) => [
        {type: 'QuickNotes' as const, id: applicantId},
      ],
      query: ({applicantId, note}) => ({
        url: `/applicants/${applicantId}/quick-notes`,
        method: 'POST',
        body: note,
      }),
    }),

    // Edits ONE note in place by id; the original author and created time are preserved server-side.
    updateQuickNote: builder.mutation<
      QuickNote,
      {applicantId: string; noteId: string; note: UpdateQuickNoteRequest}
    >({
      invalidatesTags: (_result, _error, {applicantId}) => [
        {type: 'QuickNotes' as const, id: applicantId},
      ],
      query: ({applicantId, noteId, note}) => ({
        url: `/applicants/${applicantId}/quick-notes/${noteId}`,
        method: 'PUT',
        body: note,
      }),
    }),
  }),
})

export const {
  useGetQuickNotesQuery,
  useAddQuickNoteMutation,
  useUpdateQuickNoteMutation,
  useLazyGetApplicantsQuery,
  useLazySearchApplicantsIsolatedQuery,
  useLazyGetApplicantsWithAssignmentsByEorQuery,
  useLazyGetApplicantQuery,
  useUpdateApplicantMutation,
  useAddApplicantsActivityMutation,
  useGetApplicantsExclusionListQuery,
  useLazyGetApplicantListByIdsQuery,
  useMoveApplicantToNewAgencyMutation,
  useLazyGetTransferEligibilityQuery,
  useTransferApplicantMutation,
  useLazyLockApplicantQuery,
  useLazyUnLockApplicantQuery,
  useGetApplicantInterviewQuery,
  useCreateApplicantInterviewMutation,
  useUpdateApplicantInterviewMutation,
  useUploadApplicantProfilePictureMutation,
  useDeleteApplicantProfilePictureMutation,
  useUpdateApplicantFlagMutation,
  useGetBankAccountsQuery,
  useAddBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
  useAddDeductionMutation,
  useUpdateDeductionMutation,
  useUpdateDeductionStatusMutation,
  useAddWorkHistoryBulkMutation,
  useUploadEventAttachmentMutation,
  useLazyGetEventAttachmentDownloadUrlQuery,
  useGetApplicantSkillsQuery,
  useUpdateApplicantSkillsMutation,
} = applicantsApi
